import React, { useState, useEffect, useRef } from "react";
import { FiMessageSquare, FiX, FiRefreshCcw } from "react-icons/fi";
import { IoSend } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";


const ChatBox = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [options, setOptions] = useState([]);

  const [step, setStep] = useState("start"); 
  const [userData, setUserData] = useState({
    interest: "",
    subInterest: "",
    name: "",
    email: "",
  });

  const navigate = useNavigate();
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, options]);

  useEffect(() => {
    if (isChatOpen && messages.length === 0) {
      setIsTyping(true);

      setTimeout(() => {
        setIsTyping(false);
        setMessages([{ text: "Welcome to Power Sense 👋", sender: "bot" }]);

        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            setMessages((prev) => [
              ...prev,
              { text: "How can we help you today?", sender: "bot" },
            ]);
            setOptions(["IoT Solutions", "Courses", "General Enquiry"]);
          }, 800);
        }, 600);
      }, 1000);
    }
  }, [isChatOpen, messages.length]); // ✅ FIXED

  const sendToEmail = async (data) => {
    try {
      await emailjs.send(
        "service_ujn4gvu",
        "template_dgq65xu",
        {
          name: data.name,
          email: data.email,
          company: data.interest,
          phone: "N/A",
          message: data.subInterest,
          time: new Date().toLocaleString(),
        },
        "v-3WMZ6Rafl3VBiyU"
      );
      console.log("Chat email sent ✅");
    } catch (error) {
      console.error("EMAIL ERROR ❌:", error);
    }
  };

  const handleSendMessage = async (inputOverride) => {
    const input = inputOverride || currentMessage.trim();
    if (!input) return;

    if (input === "Admin") {
      navigate("/signin");
      return;
    }

    // Only add to message UI if it's not a validation retry
    if (!(step === "email" && !/^\S+@\S+\.\S+$/.test(input))) {
        setMessages((prev) => [...prev, { text: input, sender: "user" }]);
    }
    
    setCurrentMessage("");
    setOptions([]);

    if (step === "start") {
      setUserData((prev) => ({ ...prev, interest: input }));
      setStep("sector");
      setIsTyping(true);
      
      setTimeout(() => {
        setIsTyping(false);
        if (input === "Courses") {
          setMessages((prev) => [
            ...prev,
            { text: "Great choice! Which type of session are you looking for?", sender: "bot" },
          ]);
          setOptions(["Workshop", "Online Classes", "Online Lectures"]);
        } else {
          setMessages((prev) => [
            ...prev,
            { text: "Excellent. Which specific sector are you interested in?", sender: "bot" },
          ]);
          setOptions(["Energy Management", "Smart Buildings", "Industrial IoT", "Other"]);
        }
      }, 1000);
    } 
    else if (step === "sector") {
      setUserData((prev) => ({ ...prev, subInterest: input }));
      setStep("name");
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          { text: "To provide a personalized proposal, may I have your name?", sender: "bot" },
        ]);
      }, 1000);
    }
    else if (step === "name") {
      setUserData((prev) => ({ ...prev, name: input }));
      setStep("email");
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          { text: `Nice to meet you, ${input}. What's your professional email?`, sender: "bot" },
        ]);
      }, 1000);
    } 
    else if (step === "email") {
      // FEATURE 1: Email Format Validation
      const emailRegex = /^\S+@\S+\.\S+$/;
      if (!emailRegex.test(input)) {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          setMessages((prev) => [
            ...prev,
            { text: "That doesn't look like a valid email. Please check and try again! 🧐", sender: "bot" },
          ]);
        }, 800);
        return;
      }

      const finalData = { ...userData, email: input };
      setUserData(finalData);
      setStep("done");

      await sendToEmail(finalData);

      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [
          ...prev,
          { text: "Thank you! Our specialists will reach out to you within 24 hours. Have a productive day! 🚀", sender: "bot" },
        ]);
      }, 1200);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setOptions([]);
    setStep("start");
    setIsChatOpen(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsChatOpen(!isChatOpen)}
        className="bg-gradient-to-tr from-orange-600 to-yellow-400 text-white p-4 rounded-full shadow-2xl flex items-center justify-center border border-white/20"
      >
        {isChatOpen ? <FiX size={24} /> : <FiMessageSquare size={24} />}
      </motion.button>

      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 w-[340px] md:w-[380px] bg-[#111827]/95 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-orange-500/20 to-transparent border-b border-white/5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-orange-500 to-yellow-400 flex items-center justify-center text-black font-bold">PS</div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#111827] rounded-full animate-pulse"></span>
                </div>
                <div>
                  <h3 className="text-white font-medium text-sm">Power Sense AI</h3>
                  <p className="text-[10px] text-green-400 font-bold uppercase tracking-widest">Online</p>
                </div>
              </div>
              <button onClick={resetChat} className="text-slate-400 hover:text-white transition-colors">
                <FiRefreshCcw size={16} />
              </button>
            </div>

            {/* Message Area */}
            <div className="h-[380px] overflow-y-auto p-4 space-y-4 scrollbar-hide">
              {messages.map((msg, i) => (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={i}
                  className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.sender === "user"
                      ? "bg-gradient-to-tr from-orange-500 to-yellow-500 text-black font-medium rounded-tr-none"
                      : "bg-[#1F2937] border border-white/5 text-slate-200 rounded-tl-none"
                  }`}>
                    {msg.text}
                  </div>
                </motion.div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-[#1F2937] border border-white/5 px-4 py-3 rounded-2xl rounded-tl-none flex gap-1.5">
                    {[0, 1, 2].map((dot) => (
                      <motion.span key={dot} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: dot * 0.2 }} className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                    ))}
                  </div>
                </div>
              )}

              {/* Options Section */}
              {options.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {options.map((opt, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      onClick={() => handleSendMessage(opt)}
                      className="px-4 py-1.5 rounded-full text-xs bg-white/5 border border-white/10 text-orange-400 hover:border-orange-500/50 transition-all"
                    >
                      {opt}
                    </motion.button>
                  ))}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input Bar */}
            <div className="p-4 bg-white/5 border-t border-white/10">
              <div className="relative flex items-center">
                <input
                  autoFocus
                  type="text"
                  disabled={step === "done" || options.length > 0}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                  placeholder={options.length > 0 ? "Select an option above..." : step === "done" ? "Chat completed" : "Type your reply..."}
                  className="w-full bg-[#1F2937] border border-white/10 rounded-xl py-3 px-4 pr-12 text-sm text-white focus:outline-none focus:border-orange-500/50 transition-all disabled:opacity-50"
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={step === "done" || options.length > 0}
                  className="absolute right-2 p-2 text-orange-500 hover:text-yellow-400 transition-all hover:scale-110 disabled:opacity-50"
                >
                  <IoSend size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatBox;